/**
 * WebSocket signaling helpers (rooms protocol) with optional ticket auth.
 */

export function buildSignalingUrl({ wsBase, roomId, peerId, token, ticket }) {
  if (!wsBase) {
    throw new Error("REACT_APP_SIGNALING_WS_URL is not configured");
  }
  const origin = /^wss?:\/\//i.test(wsBase) ? wsBase : `wss://${wsBase}`;
  const u = new URL(origin);
  u.pathname = `/ws/${encodeURIComponent(roomId)}/${encodeURIComponent(peerId)}`;
  if (ticket) {
    u.searchParams.set("ticket", ticket);
  } else if (token) {
    u.searchParams.set("token", token);
  }
  return u.toString();
}

/**
 * Mint a short-lived signaling ticket using a Cognito ID token.
 * POST {wsBase origin}/v1/signaling-ticket
 */
export async function fetchSignalingTicket({ wsBase, idToken, roomId }) {
  if (!wsBase) throw new Error("REACT_APP_SIGNALING_WS_URL is not configured");
  if (!idToken) throw new Error("Missing ID token for ticket mint");

  const origin = /^wss?:\/\//i.test(wsBase) ? wsBase : `wss://${wsBase}`;
  const httpBase = origin
    .replace(/^ws:/i, "http:")
    .replace(/^wss:/i, "https:")
    .replace(/\/$/, "");

  const resp = await fetch(`${httpBase}/v1/signaling-ticket`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "content-type": "application/json",
    },
    body: JSON.stringify(roomId ? { roomId } : {}),
  });
  const text = await resp.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* ignore */
  }
  if (!resp.ok) {
    throw new Error(data.detail || data.error || `Ticket mint failed (${resp.status})`);
  }
  if (!data.ticket) throw new Error("Ticket response missing ticket");
  return data;
}

/**
 * Creates a rooms-protocol WebSocket connection with join + JSON ping.
 */
export function connectSignaling({
  url,
  attendee,
  onOpen,
  onClose,
  onError,
  onMessage,
  log = () => {},
}) {
  let ws;
  let pingInterval = null;

  try {
    ws = new WebSocket(url);
  } catch (e) {
    log("WS constructor error:", e?.message || String(e));
    throw e;
  }

  const clearPing = () => {
    if (pingInterval) {
      try {
        clearInterval(pingInterval);
      } catch {
        /* ignore */
      }
      pingInterval = null;
    }
  };

  ws.onopen = () => {
    log("WS connected (rooms path)");
    ws.send(
      JSON.stringify({
        type: "join",
        attendee: {
          peerId: attendee.peerId,
          displayName: attendee.displayName || attendee.peerId,
          role: attendee.role || "client",
          avatarUrl: "",
          capabilities: ["cam", "mic"],
        },
      })
    );
    clearPing();
    pingInterval = setInterval(() => {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "ping" }));
    }, 10000);
    onOpen?.();
  };

  ws.onmessage = (ev) => {
    let msg;
    try {
      msg = JSON.parse(ev.data);
    } catch {
      return;
    }
    onMessage?.(msg);
  };

  ws.onclose = (ev) => {
    log(`WS closed (code=${ev.code}, clean=${ev.wasClean})`);
    clearPing();
    onClose?.(ev);
  };

  ws.onerror = (e) => {
    log("WS error", e?.message || String(e));
    onError?.(e);
  };

  return {
    ws,
    close() {
      clearPing();
      try {
        if (ws && ws.readyState === WebSocket.OPEN) ws.close();
      } catch {
        /* ignore */
      }
    },
    sendSignal(toPeerId, signal) {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        log("WS not open");
        return;
      }
      ws.send(JSON.stringify({ type: "signal", to: toPeerId, signal }));
    },
    send(obj) {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        log("WS not open");
        return;
      }
      ws.send(JSON.stringify(obj));
    },
    isOpen() {
      return !!(ws && ws.readyState === WebSocket.OPEN);
    },
  };
}
